<template>
  <aside class="ship-sidebar" :class="{ collapsed: isCollapsed }">
    <button
      class="sidebar-toggle"
      :title="isCollapsed ? 'Show vessels' : 'Hide vessels'"
      @click="toggleCollapse"
    >
      <i :class="isCollapsed ? 'ri-arrow-right-s-line' : 'ri-arrow-left-s-line'"></i>
    </button>

    <div class="sidebar-panel">
      <div class="sidebar-inner">
        <!-- Header -->
        <div class="sidebar-header">
          <h5 class="sidebar-title">
            Vessels <span class="vessel-count">{{ localShips.length }}</span>
          </h5>

          <div class="sidebar-search">
            <i class="ri-search-line"></i>
            <input v-model="searchQuery" type="text" placeholder="Search vessel..." />
          </div>

          <div class="sidebar-actions">
            <button class="btn btn-sm btn-mantraz" @click="showAll">
              <i class="ri-eye-line mr-1"></i>Show All
            </button>
            <button class="btn btn-sm btn-outline-mantraz" @click="hideAll">
              <i class="ri-eye-off-line mr-1"></i>Hide All
            </button>
          </div>

          <div class="status-summary">
            <span class="status-chip">
              <span class="dot online"></span>
              <span class="status-chip-count">{{ statusCounts.online }}</span>
              <span class="status-chip-label">Online</span>
            </span>
            <span class="status-chip">
              <span class="dot offline"></span>
              <span class="status-chip-count">{{ statusCounts.offline }}</span>
              <span class="status-chip-label">Offline</span>
            </span>
            <span class="status-chip">
              <span class="dot no-gps"></span>
              <span class="status-chip-count">{{ statusCounts['no-gps'] }}</span>
              <span class="status-chip-label">No GPS</span>
            </span>
            <span class="status-chip">
              <span class="dot no-sensor"></span>
              <span class="status-chip-count">{{ statusCounts['no-sensor'] }}</span>
              <span class="status-chip-label">No Sensor</span>
            </span>
          </div>
        </div>

        <!-- List -->
        <div class="vessel-list">
          <div v-if="filteredShips.length === 0" class="vessel-empty">No vessels found.</div>

          <div class="vessel-item" v-for="item in filteredShips" :key="item.massId">
            <div
              class="vessel-header"
              @click="toggleExpand(item)"
              @mouseenter="$emit('hover-ship', item.massId)"
              @mouseleave="$emit('hover-ship', null)">
              <div class="vessel-row-top">
                <div class="vessel-name">{{ item.massName }}</div>
                <span class="vessel-status-label" :class="resolveStatus(item)">{{ statusLabel(item) }}</span>
                <button
                  class="vessel-toggle-btn"
                  :class="item.isVisible ? 'is-shown' : 'is-hidden'"
                  @click.stop="toggleVisibility(item)"
                >
                  <i :class="item.isVisible ? 'ri-eye-line' : 'ri-eye-off-line'"></i>
                  {{ item.isVisible ? 'Hide' : 'Show' }}
                </button>
                <i class="vessel-chevron" :class="item.expanded ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'"></i>
              </div>
              <div class="vessel-stats">
                <span class="stat"><span class="stat-k">Speed</span>{{ speedKn(item) !== null ? speedKn(item) + ' kn' : '-' }}</span>
                <span class="stat" v-for="rpm in rpmList(item)" :key="rpm.label"><span class="stat-k">{{ rpm.label }}</span>{{ rpm.value }}</span>
                <span class="stat" v-if="rpmList(item).length === 0"><span class="stat-k">RPM</span>{{ hasRpmDevice(item) ? '-' : 'N/A' }}</span>
              </div>
            </div>

            <div class="vessel-detail" v-if="item.expanded">
              <div class="detail-pager" v-if="detailGroups(item).length">
                <div class="pager-label">
                  {{ activeGroup(item).label }}
                  <span class="pager-count">{{ activePage(item) + 1 }}/{{ detailGroups(item).length }}</span>
                </div>
                <button
                  class="pager-arrow"
                  :disabled="detailGroups(item).length < 2"
                  @click.stop="pageStep(item, -1)"
                >
                  <i class="ri-arrow-left-s-line"></i>
                </button>
                <button
                  class="pager-arrow"
                  :disabled="detailGroups(item).length < 2"
                  @click.stop="pageStep(item, 1)"
                >
                  <i class="ri-arrow-right-s-line"></i>
                </button>
              </div>

              <div class="pager-dots" v-if="detailGroups(item).length > 1">
                <span
                  v-for="(group, idx) in detailGroups(item)"
                  :key="group.key"
                  class="pager-dot"
                  :class="{ active: idx === activePage(item) }"
                  :title="group.label"
                  @click.stop="setPage(item, idx)"
                ></span>
              </div>

              <div class="detail-cards" v-if="activeGroup(item).cards">
                <div class="fuel-card" v-for="card in activeGroup(item).cards" :key="card.title">
                  <div class="fuel-card-title">{{ card.title }}</div>
                  <div class="fuel-card-metrics">
                    <div class="fuel-metric" v-for="row in card.rows" :key="row.label">
                      <span class="fuel-metric-label">{{ row.label }}</span>
                      <span class="fuel-metric-value">{{ row.value }}</span>
                    </div>
                  </div>
                  <div class="fuel-card-time" v-if="card.time">
                    <i class="ri-time-line"></i>
                    <span class="pager-time-date">{{ card.time.date }}</span>
                    <span class="pager-time-clock">{{ card.time.time }}</span>
                  </div>
                </div>
              </div>

              <template v-else-if="activeGroup(item).sections">
                <div v-for="(section, idx) in activeGroup(item).sections" :key="idx">
                  <hr v-if="idx > 0" class="section-divider" />
                  <div class="section-time" v-if="section.time">
                    <i class="ri-time-line"></i>
                    <span class="pager-time-date">{{ formatSectionTime(section.time).date }}</span>
                    <span class="pager-time-clock">{{ formatSectionTime(section.time).time }}</span>
                  </div>
                  <div class="detail-grid">
                    <div
                      class="detail-cell"
                      :class="{ 'detail-cell-full': row.full }"
                      v-for="row in section.rows"
                      :key="row.label"
                    >
                      <span class="detail-label">{{ row.label }}</span>
                      <span class="detail-value">{{ row.value }}</span>
                    </div>
                  </div>
                </div>
              </template>

              <div class="detail-grid" v-else>
                <div
                  class="detail-cell"
                  :class="{ 'detail-cell-full': row.full }"
                  v-for="row in activeGroup(item).rows"
                  :key="row.label"
                >
                  <span class="detail-label">{{ row.label }}</span>
                  <span class="detail-value">{{ row.value }}</span>
                </div>
              </div>

              <div class="pager-time" v-if="!activeGroup(item).sections && activeGroup(item).time">
                <i class="ri-time-line"></i>
                <span class="pager-time-date">{{ dataTimeParts(item).date }}</span>
                <span class="pager-time-clock">{{ dataTimeParts(item).time }}</span>
              </div>

              <div class="trail-panel">
                <div class="trail-head">
                  <b-form-checkbox
                    v-model="item.trail"
                    switch
                    @change="updateTrail({ massId: item.massId, trail: item.trail, days: item.trailDays || 1 })"
                  >Vessel Trail</b-form-checkbox>
                  <div class="trail-range">
                    <button
                      v-for="d in trailDayOptions"
                      :key="d"
                      type="button"
                      class="trail-chip"
                      :class="{ active: (item.trailDays || 1) === d }"
                      @click.stop="setTrailDays(item, d)"
                    >{{ d }}d</button>
                  </div>
                </div>
                <div class="trail-stats" v-if="item.trail && trailStats(item).length">
                  <div class="trail-stat" v-for="s in trailStats(item)" :key="s.label">
                    <span class="trail-stat-label">{{ s.label }}</span>
                    <span class="trail-stat-value">{{ s.value }}</span>
                  </div>
                </div>
                <div class="trail-hint" v-else>
                  {{ item.trail
                    ? `No trail data for the last ${item.trailDays || 1}-day window.`
                    : `Enable Vessel Trail to see the last ${item.trailDays || 1}-day summary.` }}
                </div>
              </div>

              <div class="detail-buttons">
                <button v-if="canReadSummaryAggregated" class="btn btn-sm btn-mantraz" @click="handleClickReport(item.massId)">Report</button>
                <button v-if="canReadSummaryAggregated" class="btn btn-sm btn-outline-mantraz" @click="handleClickDashboard(item.massId)">Dashboard</button>
                <button v-if="canReadVoyageMaster" class="btn btn-sm btn-outline-mantraz" @click="handleClickVoyage(item.massId)">Voyage</button>
              </div>
            </div>
          </div>
        </div>

        <!-- Map Options -->
        <div class="sidebar-footer">
          <button class="options-toggle" @click="optionsOpen = !optionsOpen">
            <i class="ri-settings-3-line"></i>
            <span>Map Options</span>
            <i :class="optionsOpen ? 'ri-arrow-down-s-line' : 'ri-arrow-up-s-line'"></i>
          </button>
          <div class="options-panel" v-if="optionsOpen">
            <template v-if="showBaseMapOptions">
              <div class="options-group-title">Base Map</div>
              <label class="options-radio">
                <input type="radio" value="satellite" v-model="mapOptions.baseLayer" @change="emitBaseLayer" />
                <span>Satellite</span>
              </label>
              <label class="options-radio">
                <input type="radio" value="vectorColor" v-model="mapOptions.baseLayer" @change="emitBaseLayer" />
                <span>Vector color</span>
              </label>
              <label class="options-radio">
                <input type="radio" value="vectorWhite" v-model="mapOptions.baseLayer" @change="emitBaseLayer" />
                <span>Vector white</span>
              </label>

              <div class="options-sep"></div>
            </template>
            <div class="options-group-title">Ship Icon</div>
            <label class="options-radio">
              <input type="radio" value="special" v-model="mapOptions.iconStyle" @change="emitIconStyle" />
              <span>Detailed</span>
              <img class="options-icon-sample" :src="detailedIconSample" alt="" />
            </label>
            <label class="options-radio">
              <input type="radio" value="simple" v-model="mapOptions.iconStyle" @change="emitIconStyle" />
              <span>Simple</span>
              <img class="options-icon-sample" :src="simpleIconSample" alt="" />
            </label>

            <div class="options-sep"></div>
            <div class="options-group-title">Icon Size</div>
            <div class="options-size">
              <input type="range" min="0.6" max="1.8" step="0.1" v-model.number="mapOptions.iconScale" @change="emitIconScale" />
              <span class="options-size-value">{{ Math.round(mapOptions.iconScale * 100) }}%</span>
            </div>

            <div class="options-sep"></div>
            <label class="options-check">
              <input type="checkbox" v-model="mapOptions.landmarks" @change="emitLandmarks" />
              <span>Landmarks</span>
            </label>
            <label class="options-check">
              <input type="checkbox" v-model="mapOptions.timezones" @change="emitTimezones" />
              <span>Timezone</span>
            </label>
          </div>
        </div>
      </div>
    </div>
  </aside>
</template>

<script>
import { getMapIconStyle, getMapIconScale } from '@/util/ship/shipIcons';
import detailedIconSample from '@/assets/images/map/tug-boat-icon.svg';
import simpleIconSample from '@/assets/images/map/online-ship.svg';
import {
  resolveStatus as resolveShipStatus,
  statusLabel as shipStatusLabel,
  speedKn as gpsSpeedKn,
  rpmList as buildRpmList,
  hasRpmDevice as shipHasRpmDevice,
  formatDataTime as splitDataTime,
  buildDetailGroups,
  buildTrailStats,
} from '@/util/ship/vesselDetail';

export default {
  name: 'ShipSection',
  props: {
    ships: {
      type: Array,
      required: true,
    },
    // Di peta Windy, base map-nya punya Windy — pilihan tile bakal berkelahi.
    showBaseMapOptions: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      isCollapsed: false,
      searchQuery: '',
      localShips: [],
      trailDayOptions: [1, 3, 7, 14],
      optionsOpen: false,
      detailedIconSample,
      simpleIconSample,
      mapOptions: {
        baseLayer: 'satellite',
        iconStyle: 'special',
        iconScale: 1,
        landmarks: false,
        timezones: false,
      },
    };
  },
  computed: {
    userAccess() {
      return this.$store.getters['auth/userAccess'];
    },
    canReadSummaryAggregated() {
      return !!this.userAccess.summary_aggregated?.includes('read');
    },
    canReadVoyageMaster() {
      return !!this.userAccess.voyage_master?.includes('read');
    },
    filteredShips() {
      const query = this.searchQuery.trim().toLowerCase();
      if (!query) return this.localShips;
      return this.localShips.filter((ship) => (ship.massName || '').toLowerCase().includes(query));
    },
    statusCounts() {
      const counts = {
        online: 0, offline: 0, 'no-gps': 0, 'no-sensor': 0,
      };
      this.localShips.forEach((ship) => {
        counts[this.resolveStatus(ship)] += 1;
      });
      return counts;
    },
  },
  watch: {
    ships: {
      handler(newShips) {
        this.localShips = newShips.map((newShip) => {
          const existingShip = this.localShips.find((local) => local.massId === newShip.massId);
          return {
            ...newShip,
            expanded: existingShip ? existingShip.expanded : false,
            detailPage: existingShip ? existingShip.detailPage : 0,
            trail: existingShip ? existingShip.trail : false,
            trailDays: existingShip ? existingShip.trailDays : 1,
            isVisible: newShip.isVisible !== undefined ? newShip.isVisible : true,
          };
        });
      },
      immediate: true,
      deep: true,
    },
  },
  mounted() {
    const savedState = localStorage.getItem('shipSidebarCollapsed');
    if (savedState !== null) {
      this.isCollapsed = JSON.parse(savedState);
    }
    this.mapOptions.baseLayer = localStorage.getItem('activeBaseLayer') || 'satellite';
    this.mapOptions.iconStyle = getMapIconStyle();
    this.mapOptions.iconScale = getMapIconScale();
  },
  methods: {
    resolveStatus(ship) {
      return resolveShipStatus(ship);
    },
    statusLabel(ship) {
      return shipStatusLabel(ship);
    },
    speedKn(ship) {
      return gpsSpeedKn(ship);
    },
    rpmList(ship) {
      return buildRpmList(ship, this.userAccess);
    },
    hasRpmDevice(ship) {
      return shipHasRpmDevice(ship);
    },
    trailStats(ship) {
      return buildTrailStats(ship);
    },
    dataTimeParts(ship) {
      return splitDataTime(this.activeGroup(ship).time) || { date: '', time: '' };
    },
    formatSectionTime(dt) {
      return splitDataTime(dt) || { date: '', time: '' };
    },
    detailGroups(ship) {
      return buildDetailGroups(ship, this.userAccess);
    },
    activePage(ship) {
      const total = this.detailGroups(ship).length;
      if (!total) return 0;
      const page = ship.detailPage || 0;
      return Math.min(Math.max(page, 0), total - 1);
    },
    activeGroup(ship) {
      const groups = this.detailGroups(ship);
      return groups[this.activePage(ship)] || { label: '', rows: [] };
    },
    pageStep(ship, dir) {
      const total = this.detailGroups(ship).length;
      if (!total) return;
      ship.detailPage = (this.activePage(ship) + dir + total) % total;
    },
    setPage(ship, idx) {
      ship.detailPage = idx;
    },
    toggleCollapse() {
      this.isCollapsed = !this.isCollapsed;
      localStorage.setItem('shipSidebarCollapsed', JSON.stringify(this.isCollapsed));
      this.$emit('toggle-collapse', this.isCollapsed);
    },
    toggleExpand(ship) {
      const wasExpanded = ship.expanded;
      this.localShips.forEach((s) => { s.expanded = false; });
      ship.expanded = !wasExpanded;
      // Buka panel = minta detail, sama kayak buka popup. Stats-nya nyusul.
      if (ship.expanded) {
        this.$emit('ship-expanded', { massId: ship.massId });
        this.zoomToShip(ship);
      }
    },
    async handleClickDashboard(massId) {
      this.$store.dispatch('filter/setCurrentMassId', massId);
      await this.$router.push({ name: 'FmsDashboard' });
    },
    async handleClickReport(massId) {
      this.$store.dispatch('filter/setCurrentMassId', massId);
      await this.$router.push({ name: 'ReportWrapper', query: { menu: 'ReportSummary' } });
    },
    async handleClickVoyage(massId) {
      await this.$router.push({ name: 'Voyage', query: { asset: massId } });
    },
    updateTrail({ massId, trail, days }) {
      this.$emit('toggle-trail', { massId, trail, days: days || 1 });
    },
    setTrailDays(item, days) {
      if ((item.trailDays || 1) === days) return;
      item.trailDays = days;
      // Cuma refetch kalo trail-nya lagi nyala; kalo mati cukup simpen preferensi buat nanti.
      if (item.trail) {
        this.$emit('trail-range', { massId: item.massId, days });
      }
    },
    toggleVisibility(item) {
      item.isVisible = !item.isVisible;
      this.$emit('toggle-visibility', { massId: item.massId, isVisible: item.isVisible });
    },
    showAll() {
      this.$emit('show-all-ships');
    },
    hideAll() {
      this.$emit('hide-all-ships');
    },
    zoomToShip(item) {
      this.$emit('zoom-to-ship', item);
    },
    emitBaseLayer() {
      this.$emit('set-base-layer', this.mapOptions.baseLayer);
    },
    emitIconStyle() {
      this.$emit('set-icon-style', this.mapOptions.iconStyle);
    },
    emitIconScale() {
      this.$emit('set-icon-scale', this.mapOptions.iconScale);
    },
    emitLandmarks() {
      this.$emit('toggle-landmarks', this.mapOptions.landmarks);
    },
    emitTimezones() {
      this.$emit('toggle-timezones', this.mapOptions.timezones);
    },
  },
};
</script>

<style scoped>
.ship-sidebar {
  position: relative;
  flex: 0 0 auto;
  width: 380px;
  height: 100%;
  transition: width 0.3s ease;
  z-index: 1000;
}

.ship-sidebar.collapsed {
  width: 0;
}

.sidebar-panel {
  width: 100%;
  height: 100%;
  overflow: hidden;
  background: #ffffff;
  border-right: 1px solid #e8ecf3;
  box-shadow: 2px 0 12px rgba(15, 23, 42, 0.06);
}

.sidebar-inner {
  width: 380px;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.sidebar-toggle {
  position: absolute;
  top: 50%;
  right: -24px;
  transform: translateY(-50%);
  width: 24px;
  height: 56px;
  border: 1px solid #e8ecf3;
  border-left: none;
  background: #ffffff;
  border-radius: 0 10px 10px 0;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #264284;
  box-shadow: 2px 0 8px rgba(15, 23, 42, 0.08);
  z-index: 1001;
}

.sidebar-toggle i {
  font-size: 20px;
}

/* Header */
.sidebar-header {
  flex: 0 0 auto;
  padding: 14px 16px;
  border-bottom: 1px solid #eef1f6;
}

.sidebar-title {
  font-size: 16px;
  font-weight: 700;
  color: #1f2937;
  margin: 0 0 10px;
}

.vessel-count {
  display: inline-block;
  margin-left: 6px;
  padding: 1px 8px;
  background: #ff69b4;
  color: #ffffff;
  border-radius: 10px;
  font-size: 12px;
  font-weight: 700;
}

.sidebar-search {
  display: flex;
  align-items: center;
  gap: 8px;
  background: #f3f4f6;
  border-radius: 20px;
  padding: 7px 12px;
  margin-bottom: 10px;
}

.sidebar-search i {
  color: #6b7280;
}

.sidebar-search input {
  border: none;
  background: transparent;
  width: 100%;
  outline: none;
  font-size: 14px;
  color: #1f2937;
}

.sidebar-actions {
  display: flex;
  gap: 8px;
  margin-bottom: 10px;
}

.sidebar-actions .btn {
  flex: 1;
}

.status-summary {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 6px 10px;
}

.status-chip {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #4b5563;
}

.status-chip-count {
  min-width: 14px;
  font-weight: 700;
  color: #1f2937;
}

.status-chip-label {
  flex: 1;
  font-weight: 600;
}

/* Status dots */
.dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
  display: inline-block;
}

.dot.online { background: var(--mz-ship-online-dot); }
.dot.offline { background: var(--mz-ship-offline); }
.dot.no-gps { background: var(--mz-ship-no-gps); }
.dot.no-sensor { background: var(--mz-ship-no-sensor); }

/* List */
.vessel-list {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  padding: 8px;
}

.vessel-empty {
  text-align: center;
  color: #9ca3af;
  padding: 24px 0;
  font-size: 13px;
}

.vessel-item {
  border: 2px solid #d3d9e3;
  border-radius: 12px;
  margin-bottom: 8px;
  background: #ffffff;
  overflow: hidden;
}

.vessel-header {
  padding: 10px 12px;
  cursor: pointer;
}

.vessel-header:hover {
  background: #fafbfc;
}

.vessel-row-top {
  display: flex;
  align-items: center;
  gap: 8px;
}

.vessel-name {
  flex: 1;
  min-width: 0;
  font-size: 14px;
  font-weight: 700;
  color: #1f2937;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.vessel-stats {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 14px;
  margin-top: 6px;
  font-size: 12px;
  font-weight: 700;
  color: #1f2937;
}

.stat {
  white-space: nowrap;
}

.stat-k {
  color: #6b7280;
  font-weight: 500;
  margin-right: 4px;
}

.vessel-status-label {
  font-size: 10px;
  font-weight: 700;
  padding: 2px 6px;
  border-radius: 6px;
  text-transform: uppercase;
  white-space: nowrap;
}

.vessel-status-label.online { background: var(--mz-chip-online-bg); color: var(--mz-chip-online-fg); }
.vessel-status-label.offline { background: var(--mz-chip-offline-bg); color: var(--mz-chip-offline-fg); }
.vessel-status-label.no-gps { background: var(--mz-chip-no-gps-bg); color: var(--mz-chip-no-gps-fg); }
.vessel-status-label.no-sensor { background: var(--mz-chip-no-sensor-bg); color: var(--mz-chip-no-sensor-fg); }

.vessel-chevron {
  color: #9ca3af;
  font-size: 18px;
}

.vessel-toggle-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  border: 1px solid #ff69b4;
  background: #ffffff;
  color: #ff69b4;
  border-radius: 14px;
  padding: 3px 10px;
  font-size: 11px;
  font-weight: 700;
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.15s ease, color 0.15s ease, border-color 0.15s ease;
}

.vessel-toggle-btn i {
  font-size: 13px;
}

.vessel-toggle-btn:hover {
  background: #ff69b4;
  color: #ffffff;
}

.vessel-toggle-btn.is-hidden {
  border-color: #c4c9d2;
  color: #6b7280;
}

.vessel-toggle-btn.is-hidden:hover {
  background: #6b7280;
  border-color: #6b7280;
  color: #ffffff;
}

/* Detail */
.vessel-detail {
  padding: 4px 12px 12px;
  border-top: 1px solid #f1f3f7;
}

/* Detail pager */
.detail-pager {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 8px;
  margin-top: 8px;
}

.pager-arrow {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border: 1px solid #e2e6ee;
  background: #ffffff;
  border-radius: 8px;
  color: #264284;
  cursor: pointer;
  flex-shrink: 0;
}

.pager-arrow:hover:not(:disabled) {
  background: #f3f5fa;
}

.pager-arrow:disabled {
  opacity: 0.4;
  cursor: default;
}

.pager-arrow i {
  font-size: 18px;
}

.pager-label {
  flex: 1;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #1f2937;
}

.pager-count {
  margin-left: 6px;
  font-size: 11px;
  font-weight: 600;
  color: #9ca3af;
}

.pager-dots {
  display: flex;
  justify-content: flex-start;
  gap: 6px;
  margin-top: 6px;
}

.pager-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #d3d9e3;
  cursor: pointer;
  transition: background 0.15s ease, transform 0.15s ease;
}

.pager-dot.active {
  background: #ff69b4;
  transform: scale(1.25);
}

.pager-time {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 5px;
  margin-top: 2px;
  padding-top: 8px;
  border-top: 1px solid #f1f3f7;
  font-size: 11px;
  color: #1f2937;
}

.pager-time i {
  font-size: 13px;
}

.pager-time-clock {
  font-weight: 700;
  color: #1f2937;
}

.pager-time-date {
  font-weight: 700;
  color: #1f2937;
}

.section-divider {
  border: none;
  border-top: 1px dashed #e6eaf1;
  margin: 8px 0;
}

.section-time {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  color: #8a93a5;
  margin-bottom: 6px;
}

.section-time i {
  font-size: 12px;
}

.section-time span {
  font-weight: 700;
  color: #1f2937;
}

/* Fuel cards */
.detail-cards {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin: 10px 0;
}

.fuel-card {
  border: 1px solid #e6eaf1;
  border-radius: 10px;
  background: #fafbfc;
  padding: 8px 10px;
}

.fuel-card-title {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: #264284;
  margin-bottom: 6px;
}

.fuel-card-metrics {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 6px;
}

.fuel-metric {
  display: flex;
  flex-direction: column;
}

.fuel-metric-label {
  font-size: 10px;
  color: #6b7280;
}

.fuel-metric-value {
  font-size: 13px;
  font-weight: 600;
  color: #1f2937;
}

.fuel-card-time {
  display: flex;
  align-items: center;
  gap: 5px;
  margin-top: 8px;
  padding-top: 6px;
  border-top: 1px dashed #e6eaf1;
  font-size: 10px;
  color: #1f2937;
}

.fuel-card-time i {
  font-size: 12px;
  color: #6b7280;
}

.detail-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px 10px;
  margin: 10px 0;
}

.detail-cell {
  display: flex;
  flex-direction: column;
}

.detail-cell-full {
  grid-column: 1 / -1;
}

.detail-label {
  font-size: 11px;
  color: #6b7280;
}

.detail-value {
  font-size: 13px;
  font-weight: 600;
  color: #1f2937;
}

.trail-panel {
  margin-bottom: 10px;
  font-size: 13px;
}

.trail-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.trail-range {
  display: flex;
  gap: 4px;
}

.trail-chip {
  min-width: 30px;
  padding: 2px 7px;
  border: 1px solid #e6eaf1;
  border-radius: 6px;
  background: #ffffff;
  font-size: 11px;
  font-weight: 600;
  color: #6b7280;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
}

.trail-chip:hover {
  border-color: #264284;
  color: #264284;
}

.trail-chip.active {
  background: #264284;
  border-color: #264284;
  color: #ffffff;
}

.trail-stats {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 6px 12px;
  margin-top: 10px;
  padding: 10px;
  border: 1px solid #e6eaf1;
  border-radius: 10px;
  background: #fafbfc;
}

.trail-stat {
  display: flex;
  flex-direction: column;
  gap: 1px;
}

.trail-stat-label {
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  color: #8a93a5;
}

.trail-stat-value {
  font-size: 13px;
  font-weight: 700;
  color: #1f2937;
}

.trail-hint {
  margin-top: 8px;
  font-size: 11px;
  font-style: italic;
  color: #8a93a5;
}

.detail-buttons {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 6px;
}

.detail-buttons .btn {
  font-size: 12px;
}

/* Map Options footer */
.sidebar-footer {
  flex: 0 0 auto;
  border-top: 1px solid #eef1f6;
}

.options-toggle {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  border: none;
  background: #ffffff;
  padding: 12px 16px;
  font-size: 13px;
  font-weight: 700;
  color: #1f2937;
  cursor: pointer;
}

.options-toggle > span {
  flex: 1;
  text-align: left;
}

.options-toggle i {
  font-size: 18px;
  color: #6b7280;
}

.options-panel {
  padding: 0 16px 14px;
  max-height: 320px;
  overflow-y: auto;
}

.options-group-title {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #6b7280;
  margin: 6px 0;
}

.options-radio,
.options-check {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #374151;
  cursor: pointer;
  margin: 0 0 6px;
}

.options-radio > span,
.options-check > span {
  flex: 1;
}

.options-radio input,
.options-check input {
  margin: 0;
  accent-color: #ff69b4;
}

.options-icon-sample {
  height: 22px;
  width: auto;
}

.options-sep {
  height: 1px;
  background: #eef1f6;
  margin: 8px 0;
}

.options-size {
  display: flex;
  align-items: center;
  gap: 8px;
}

.options-size input[type="range"] {
  flex: 1;
  accent-color: #ff69b4;
  cursor: pointer;
}

.options-size-value {
  font-size: 12px;
  font-weight: 700;
  color: #1f2937;
  min-width: 38px;
  text-align: right;
}

/* On small screens overlay the sidebar over the map instead of squashing it */
@media (max-width: 768px) {
  .ship-sidebar {
    position: absolute;
    left: 0;
    top: 0;
  }
  .ship-sidebar:not(.collapsed) {
    width: min(380px, 88vw);
  }
  .sidebar-inner {
    width: min(380px, 88vw);
  }
}
</style>
